import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Popover,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Quickreply as QuickreplyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

interface QuickMessage {
  id: string;
  name: string;
  message: string;
}

interface QuickMessagesButtonProps {
  onSelectMessage: (message: string) => void;
  channel?: 'whatsapp' | 'telegram' | 'instagram' | 'whatsapp_recovery';
}

const QuickMessagesButton: React.FC<QuickMessagesButtonProps> = ({
  onSelectMessage,
  channel = 'whatsapp',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [messages, setMessages] = useState<QuickMessage[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState<QuickMessage | null>(null);
  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const storageKey = `quick_messages_${channel}`;

  // Carregar mensagens do localStorage
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        setMessages(JSON.parse(stored));
      } catch (error) {
        console.error('Erro ao carregar mensagens rápidas:', error);
      }
    }
  }, [storageKey]);

  // Salvar mensagens no localStorage
  const saveMessages = (newMessages: QuickMessage[]) => {
    setMessages(newMessages);
    localStorage.setItem(storageKey, JSON.stringify(newMessages));
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectMessage = (message: string) => {
    onSelectMessage(message);
    handleClose();
  };

  const handleOpenDialog = (message?: QuickMessage) => {
    if (message) {
      setEditingMessage(message);
      setFormName(message.name);
      setFormMessage(message.message);
    } else {
      setEditingMessage(null);
      setFormName('');
      setFormMessage('');
    }
    setOpenDialog(true);
    setMenuAnchorEl(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMessage(null);
    setFormName('');
    setFormMessage('');
  };

  const handleSaveMessage = () => {
    if (!formName.trim() || !formMessage.trim()) {
      return;
    }

    const newMessages = [...messages];
    if (editingMessage) {
      const index = newMessages.findIndex((m) => m.id === editingMessage.id);
      if (index !== -1) {
        newMessages[index] = {
          id: editingMessage.id,
          name: formName.trim(),
          message: formMessage.trim(),
        };
      }
    } else {
      newMessages.push({
        id: Date.now().toString(),
        name: formName.trim(),
        message: formMessage.trim(),
      });
    }
    saveMessages(newMessages);
    handleCloseDialog();
  };

  const handleDeleteMessage = (id: string) => {
    const newMessages = messages.filter((m) => m.id !== id);
    saveMessages(newMessages);
    setMenuAnchorEl(null);
    setSelectedMessageId(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, messageId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMessageId(messageId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMessageId(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'quick-messages-popover' : undefined;

  return (
    <>
      <Tooltip title="Mensagens Rápidas">
        <IconButton
          onClick={handleClick}
          size="medium"
          sx={{
            color: '#8b5cf6',
            flexShrink: 0,
            '&:hover': {
              color: '#7c3aed',
              backgroundColor: 'rgba(139, 92, 246, 0.15)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <QuickreplyIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(139, 92, 246, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.18)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(139, 92, 246, 0.05)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#000000', fontSize: '1rem', fontWeight: 600 }}>
              Mensagens Rápidas
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                color: '#8b5cf6',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              Nova
            </Button>
          </Box>

          <Divider sx={{ mb: 1, borderColor: 'rgba(139, 92, 246, 0.1)' }} />

          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Nenhuma mensagem rápida criada
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  mt: 2,
                  color: '#8b5cf6',
                  '&:hover': {
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                  },
                }}
              >
                Criar primeira mensagem
              </Button>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, message.id)}
                      sx={{ color: '#64748b' }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => handleSelectMessage(message.message)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#000000',
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          {message.name}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {message.message}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {/* Menu de ações (editar/excluir) */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(139, 92, 246, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.18)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(139, 92, 246, 0.05)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const message = messages.find((m) => m.id === selectedMessageId);
            if (message) {
              handleOpenDialog(message);
            }
          }}
          sx={{
            color: '#475569',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedMessageId) {
              handleDeleteMessage(selectedMessageId);
            }
          }}
          sx={{
            color: '#ff4444',
            '&:hover': {
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
            },
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          Excluir
        </MenuItem>
      </Menu>

      {/* Dialog para criar/editar mensagem */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(139, 92, 246, 0.08) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(139, 92, 246, 0.18)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(139, 92, 246, 0.05)',
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: '#000000' }}>
          {editingMessage ? 'Editar Mensagem Rápida' : 'Nova Mensagem Rápida'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            variant="outlined"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: '#475569',
                '& fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8b5cf6',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
              },
            }}
          />
          <TextField
            margin="dense"
            label="Mensagem"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={formMessage}
            onChange={(e) => setFormMessage(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#475569',
                '& fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(139, 92, 246, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8b5cf6',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(139, 92, 246, 0.1)' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveMessage}
            variant="contained"
            disabled={!formName.trim() || !formMessage.trim()}
            sx={{
              backgroundColor: '#14b8a6',
              '&:hover': {
                backgroundColor: '#0d9488',
              },
            }}
          >
            {editingMessage ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickMessagesButton;

